import { css, html, ref, when } from '@genesislcap/web-core';
import { cancelButtonBg } from '../../../styles';
import { DealSeries } from './deal-series';
import { DealSeriesActionIcon } from '../cell-renderers/deal-series-action-icon';
import { MaturityAmountEntry } from '../cell-renderers/maturity-amount-entry';
import { customEvent } from '@genesislcap/foundation-events';
import { DealConditionsForm } from './deal-conditions-form/deal-conditions-form';
import { SinkingFundModal } from './sinking-fund-modal/sinking-fund-modal';
import { PrincipalAmountModal } from './principal-amount-modal/principal-amount-modal';
import { SplitOrderModal } from './split-order-modal/split-order-modal';
import { SlottedStyles } from '@genesislcap/foundation-utils';
DealSeriesActionIcon;
MaturityAmountEntry;
DealConditionsForm;
SinkingFundModal;
PrincipalAmountModal;
SplitOrderModal;
SlottedStyles;

const slottedStyles = css`
  .grouped-column {
    border-right: 1px solid ${cancelButtonBg};
  }

  .ag-cell.errored-cell,
  .ag-ltr .ag-cell.errored-cell {
    border: 1px solid var(--error-color);
  }

  /* Normal hover effect - white border (only on non-error/warning cells) */
  .ag-cell.hover-effect:hover {
      border: 1px solid rgb(255, 255, 255) !important;
      box-sizing: border-box !important;
  }

  /* Error cells - red border always, even on hover */
  .ag-cell.quantity-error {
      border: 1px solid var(--error-color, #ff4444) !important;
      box-sizing: border-box !important;
  }

  .ag-cell.quantity-error:hover {
      border: 1px solid var(--error-color, #ff4444) !important;
      box-sizing: border-box !important;
  }

  /* Warning cells - amber border always, even on hover */
  .ag-cell.quantity-warning {
      border: 1px solid #ffb660 !important;
      box-sizing: border-box !important;
  }

  .ag-cell.quantity-warning:hover {
      border: 1px solid #ffb660 !important;
      box-sizing: border-box !important;
  }

  /* Focus effect - ONLY on cells with hover-effect class (Order Amount & Conditions Summary) */
  .ag-cell.hover-effect:focus-within {
    border: 1px solid var(--ag-range-selection-border-color) !important;
    background-color: #181d1f !important;
  }

  /* But if there's an error, keep red border on focus */
  .ag-cell.quantity-error:focus-within {
      border: 1px solid var(--error-color, #ff4444) !important;
      box-sizing: border-box !important;
  }

  /* If there's a warning, keep amber border on focus */
  .ag-cell.quantity-warning:focus-within {
      border: 1px solid #ffb660 !important;
      box-sizing: border-box !important;
  }

  /* Highlight the Conditions Summary cell when its modal is open */
  .ag-row.conditions-modal-open .conditions-summary-cell {
      border: 1px solid var(--accent-fill-rest, #1f2126) !important;
      box-sizing: border-box !important;
  }

  .ag-cell-focus {
    border: none !important;
  }

  .focus-border {
    border: 1px solid #505050 !important;
  }

  .no-selection {
    pointer-events: none;
  }

  .ag-cell.term-bond-par-amount {
    color: #4da3ff !important;
  }

  /* AG Grid defaults body viewport to min 150px; unset so shrink-wrap height matches row count */
  .ag-center-cols-clipper {
    min-height: unset !important;
  }

  /* Horizontal scrollbar below pinned Totals row (normal + autoHeight layouts) */
  .ag-root.ag-layout-normal,
  .ag-root.ag-layout-auto-height {
    display: flex;
    flex-direction: column;
  }

  .ag-floating-bottom {
    order: 2;
    flex-shrink: 0;
  }

  .ag-body-horizontal-scroll {
    order: 3;
    flex-shrink: 0;
  }

  .ag-floating-bottom .ag-body-horizontal-scroll-viewport,
  .ag-floating-bottom-viewport {
    overflow-x: hidden !important;
  }
`;

export const dealSeriesTemplate = html<DealSeries>`
  <div class="deal-grid">
    <deal-series-information 
      :series="${(x) => x.series}"
      :bondSeriesList="${(x) => x.bondSeriesList}"
    ></deal-series-information>
    <div class="deal-grid-container" ${ref('gridContainer')}>
      <rapid-grid-pro
        @rowDataUpdated="${x => x.updateTotalsRow()}"
        @cellValueChanged="${x => x.updateTotalsRow()}"
        @gridReady="${(x, ctx) => x.handleGridReady(customEvent(ctx))}"
        :gridOptions="${(x) => x.gridOptions}"
      >
        <slotted-styles :styles="${() => slottedStyles}"></slotted-styles>
        <grid-pro-genesis-datasource
          criteria="${x => x.criteria}"
          resource-name="ALL_MATURITY_ORDERS"
          order-by="MATURITY_DATE"
        </grid-pro-genesis-datasource>
        
      </rapid-grid-pro>
    </div>
    <sinking-fund-modal
      ?visible="${(x) => x.sinkingFundModalVisible}"
      schedule-json="${(x) => x.sinkingFundScheduleJson ?? ''}"
      panel-style="${(x) => x.sinkingFundPanelStyle}"
      @panel-mouseenter="${(x) => x.handleSinkingFundPanelMouseEnter()}"
      @panel-mouseleave="${(x) => x.handleSinkingFundPanelMouseLeave()}"
    ></sinking-fund-modal>
    <principal-amount-modal
      ?visible="${(x) => x.principalAmountModalVisible}"
      principal-amount="${(x) => x.principalAmountValue ?? ''}"
      ?value-highlighted="${(x) => x.principalAmountHighlighted}"
      anchor-top="${(x) => x.principalAmountAnchorTop}"
      anchor-left="${(x) => x.principalAmountAnchorLeft}"
    ></principal-amount-modal>
    <split-order-modal
      ${ref('splitOrderModal')}
      series-code="${(x) => x.series?.SERIES_CODE ?? ''}"
      series-size="${(x) => x.series?.SERIES_SIZE ?? ''}"
      tax-status="${(x) => x.series?.TAX_STATUS ?? ''}"
      security-type="${(x) => x.series?.SECURITY_TYPE ?? ''}"
      deal-status="${(x) => x.dealStatus}"
      ?deal-has-eligible-orders-to-reset="${(x) => x.dealHasEligibleOrdersToReset}"
      :flexMaturityOptions="${(x) => x.flexMaturityOptions || []}"
      @save-split="${(x, ctx) => x.handleSaveSplit(customEvent(ctx))}"
      @remove-split="${(x, ctx) => x.handleRemoveSplit(customEvent(ctx))}"
      @split-line-changed="${(x, ctx) => x.handleSplitLineChanged(customEvent(ctx))}"
    ></split-order-modal>
  </div>
  ${when(x => x.conditionsActive && x.orderEditConditions, html<DealSeries>`
    <div class="conditions-popup" ${ref('conditionsPopup')}>
      <div class="conditions-header">
        <div>Conditions</div>
        <rapid-button class="close-icon" aria-label="close" appearance="stealth" @click="${x => x.closeConditionsPopup()}">
          <rapid-icon name="times" size="lg"></rapid-icon>
        </rapid-button>
      </div>
      <div class="conditions-form">
        <deal-conditions-form
          series-code="${x => x.series.SERIES_CODE}"
          series-size="${x => x.series.SERIES_SIZE}"
          tax-status="${x => x.series.TAX_STATUS}"
          security-type="${x => x.series.SECURITY_TYPE}"
          deal-status="${x => x.dealStatus}"
          @orderChanged="${(x, ctx) => x.handleOrderConditionsChanged(customEvent(ctx))}"
          :munisOrder="${x => x.orderEditConditions}"
          :flexMaturityOptions="${x => x.flexMaturityOptions || []}">
        </deal-conditions-form>
      </div>
    </div>
  `)}
  <rapid-modal 
    :onCloseCallback="${(x) => () => x.cancelClearAllOrders()}"
    ${ref('clearAllOrdersModal')}>
    <h3 slot="top">${(x) => x.clearAllModalHeader}</h3>
    <p>${(x) => x.clearAllModalBody}<br>${(x) => x.clearAllModalBodySecondLine}</p>
    <div slot="bottom">
      <rapid-button @click="${(x) => x.cancelClearAllOrders()}">No, keep</rapid-button>
      <rapid-button appearance="primary" @click="${(x) => x.clearAllDraftOrders()}">
        ${(x) => x.clearAllModalButtonText}
      </rapid-button>
    </div>
  </rapid-modal> 
  <rapid-modal 
    :onCloseCallback="${(x) => () => x.cancelUpdateAllOrders()}"
    ${ref('allOrdersUpdateConfirmModal')}>
    <h3 slot="top">Confirm Update All</h3>
    <p>Are you sure you want all available amounts for all available maturities?<br>Orders In Transit will not be changed.</p>
    <div slot="bottom">
      <rapid-button @click="${(x) => x.cancelUpdateAllOrders()}">Cancel</rapid-button>
      <rapid-button appearance="primary" @click="${(x) => x.confirmUpdateAllOrders()}">
        Yes, Apply to All
      </rapid-button>
    </div>
  </rapid-modal>
`;
